'use client';

import type { AttachmentView } from '@components/files/AttachmentGallery';
import { MAX_FILES_PER_PARENT, type FileParentType } from '@files/constants';
import {
  prepareLocalFiles,
  revokePreview,
  uploadPendingFile,
  type FileValidationCode,
  type PendingLocalFile,
  type UploadTargetContext
} from '@files/upload-client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { runZeroMutation } from '../../zero/run-mutation';
import { useAppZero } from '../../zero/use-app-zero';
import { useCurrentOrgUser } from '../../zero/use-current-user';

export type ParentFileUploadOptions = {
  organizationId: string;
  parentId: string;
  parentType: Extract<
    FileParentType,
    'menus' | 'recipes' | 'ingredients' | 'knowledge_articles'
  >;
  /** Files already stored on the parent; counts toward the per-parent cap. */
  existingCount: number;
  canEdit: boolean;
  onBeforeUpload?: () => Promise<boolean>;
  /** Rejects picked files (as unsupported) before they are prepared. */
  acceptFile?: (file: File) => boolean;
  /** Transforms each accepted file first (HEIC conversion, downscaling). */
  prepareFile?: (file: File) => Promise<File>;
  /** Overrides the generic validation copy for this picker. */
  validationMessages?: Partial<Record<FileValidationCode, string>>;
  /** Called with each file id once its `files` row is saved. */
  onUploaded?: (fileId: string) => void;
};

/**
 * Pick → upload to blob storage → persist the `files` row, with retry for
 * uploads whose row could not be saved. Shared by the generic attachment
 * list and the menu cover photo card.
 */
export function useParentFileUpload({
  organizationId,
  parentId,
  parentType,
  existingCount,
  canEdit,
  onBeforeUpload,
  acceptFile,
  prepareFile,
  validationMessages,
  onUploaded
}: ParentFileUploadOptions) {
  const t = useTranslations('files');
  const z = useAppZero();
  const orgUser = useCurrentOrgUser(organizationId);
  const uploadingRef = useRef(false);
  const preparingRef = useRef(false);
  const [preparing, setPreparing] = useState(false);
  const pendingCleanupRef = useRef<PendingLocalFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingLocalFile[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  pendingCleanupRef.current = pending;

  useEffect(() => () => pendingCleanupRef.current.forEach(revokePreview), []);

  const target: UploadTargetContext = {
    organizationId,
    parentId,
    parentType
  };

  /** Uploads and saves the queue in order; resolves to the saved ids. */
  const persistPending = async (
    queue: PendingLocalFile[]
  ): Promise<string[]> => {
    const saved: string[] = [];
    if (!canEdit || uploadingRef.current || !queue.length) {
      return saved;
    }
    uploadingRef.current = true;
    let remaining = [...queue];
    try {
      // Busy from the first await, so a form can't be saved mid-upload.
      setUploading(true);
      if (onBeforeUpload && !(await onBeforeUpload())) {
        // Keep the files queued and say so, so Retry is offered.
        setError(t('upload-error'));
        return saved;
      }
      setError(null);
      for (const candidate of queue) {
        const uploaded = await uploadPendingFile(candidate, target, value =>
          setProgress(current => ({ ...current, [candidate.id]: value }))
        );
        const retained = {
          ...candidate,
          ...uploaded,
          localFile: undefined
        };
        remaining = remaining.map(file =>
          file.id === candidate.id ? retained : file
        );
        setPending(remaining);
        const persisted = await runZeroMutation(
          z.mutate.files.create({
            ...uploaded,
            organizationId,
            parentId,
            parentType,
            uploadedByUserId: orgUser?.userId,
            now: Date.now()
          }),
          { errorTitle: t('upload-error') }
        );
        if (!persisted) {
          throw new Error('persist-failed');
        }
        saved.push(uploaded.id);
        onUploaded?.(uploaded.id);
        revokePreview(candidate);
        remaining = remaining.filter(file => file.id !== candidate.id);
        setPending(remaining);
      }
    } catch {
      setPending(remaining);
      setError(t('upload-error'));
    } finally {
      uploadingRef.current = false;
      setProgress({});
      setUploading(false);
    }
    return saved;
  };

  const addFiles = async (list: File[]): Promise<string[]> => {
    if (!canEdit || uploadingRef.current || preparingRef.current) {
      return [];
    }
    const accepted = acceptFile ? list.filter(acceptFile) : list;
    const rejected: FileValidationCode[] =
      accepted.length < list.length ? ['unsupported-type'] : [];
    // Conversion can take seconds; report busy while it runs.
    preparingRef.current = true;
    setPreparing(true);
    let result: Awaited<ReturnType<typeof prepareLocalFiles>>;
    try {
      const transformed: File[] = [];
      for (const file of accepted) {
        try {
          transformed.push(prepareFile ? await prepareFile(file) : file);
        } catch {
          rejected.push('unsupported-type');
        }
      }
      result = await prepareLocalFiles(
        transformed,
        MAX_FILES_PER_PARENT - existingCount - pending.length
      );
    } finally {
      preparingRef.current = false;
      setPreparing(false);
    }
    const errors = [...new Set([...rejected, ...result.errors])];
    if (errors.length) {
      setError(
        errors
          .map(
            code =>
              validationMessages?.[code] ??
              t(`validation-${code}` as Parameters<typeof t>[0])
          )
          .join(' ')
      );
    }
    if (!result.files.length) {
      return [];
    }
    const queue = [...pending, ...result.files];
    setPending(queue);
    return persistPending(queue);
  };

  const removePending = (id: string) => {
    setPending(current => {
      const removed = current.find(file => file.id === id);
      if (removed) {
        revokePreview(removed);
      }
      return current.filter(file => file.id !== id);
    });
    setError(null);
  };

  const removeFile = (file: Pick<AttachmentView, 'id'>) =>
    runZeroMutation(z.mutate.files.remove({ id: file.id, organizationId }), {
      errorTitle: t('remove-error')
    });

  const pendingViews: AttachmentView[] = pending.map(file => ({
    id: file.id,
    url: '',
    previewUrl: file.previewUrl,
    filename: file.filename,
    contentType: file.contentType,
    sizeBytes: file.sizeBytes,
    width: file.width,
    height: file.height
  }));

  return {
    pending,
    pendingViews,
    uploading: uploading || preparing,
    progressPercent: Math.round(Math.max(0, ...Object.values(progress), 0)),
    error,
    atLimit: existingCount + pending.length >= MAX_FILES_PER_PARENT,
    addFiles,
    retry: () => persistPending(pending),
    removePending,
    removeFile
  };
}
